import { create } from 'zustand'
import { GhostState, DefaultDurations, DEFAULT_DURATIONS } from './types'

interface DispositionDraft {
  workOrderId: string
  assignedTechnicianId: number
  date: string // anfahrtStartIso (travel start)
  tradeCode: number
  plannedTravelDuration: number
  plannedWorkDuration: number
  plannedDepartureDuration: number
  description: string | null
}

interface DispositionDraftState {
  // Ghost block on the timeline
  ghost: GhostState | null

  // Durations — shared between timeline ghost and panel
  durations: DefaultDurations

  // Finalized draft (after ghost is committed) — used by panel
  draft: DispositionDraft | null
  technicianName: string | null

  // Full-day planning mode
  isFullDay: boolean

  // True when the ghost overlaps another event or falls outside working hours/shift
  isGhostInvalid: boolean

  // Actions — ghost
  setGhost: (ghost: GhostState | null) => void
  commitGhost: () => void
  clearGhost: () => void
  setGhostInvalid: (v: boolean) => void

  // Actions — durations (shared)
  setDurations: (durations: DefaultDurations) => void
  resetDurations: (durations: DefaultDurations) => void

  // Actions — draft
  finalizeDraft: (params: {
    technicianId: string
    technicianName: string | null
    anfahrtStartIso: string
    durations: DefaultDurations
    workOrderId: string
    tradeCode: number
    description?: string | null
    isFullDay?: boolean
  }) => void
  updatePosition: (anfahrtStartIso: string) => void
  updateTradeCode: (tradeCode: number) => void
  updateDescription: (description: string | null) => void

  // Actions — full-day toggle
  setFullDay: (v: boolean, params: { shiftStart: string | null; shiftEnd: string | null }) => void

  // Actions — move full-day ghost to a different technician
  moveFullDayToTechnician: (params: {
    technicianId: number
    technicianName: string | null
    shiftStart: string | null
    shiftEnd: string | null
  }) => void

  // Actions — full reset
  clearAll: () => void
}

/**
 * Builds anfahrtStart ISO for a given day using the shiftStart time.
 * shiftStart is stored as "local time labelled UTC" (e.g. 07:00 local → T07:00Z),
 * so we read with getUTC* and construct a local Date — matching resolveBreakForDay.
 */
function buildFullDayAnfahrtStart(dayIso: string, shiftStart: string | null): string {
  const [y, m, d] = dayIso.split('-').map(Number)
  if (!shiftStart) return new Date(y, m - 1, d, 6, 0, 0, 0).toISOString()
  const s = new Date(shiftStart)
  return new Date(y, m - 1, d, s.getUTCHours(), s.getUTCMinutes(), 0, 0).toISOString()
}

/** Work duration = shift length − 30 (travel) − 30 (departure) */
function buildFullDayWorkDuration(shiftStart: string | null, shiftEnd: string | null): number {
  if (!shiftStart || !shiftEnd) return 900 // fallback: 960 min (6–22) − 60
  const start = new Date(shiftStart)
  const end = new Date(shiftEnd)
  const shiftMinutes =
    (end.getUTCHours() * 60 + end.getUTCMinutes()) -
    (start.getUTCHours() * 60 + start.getUTCMinutes())
  return Math.max(60, shiftMinutes - 60)
}

export const useDispositionDraftStore = create<DispositionDraftState>((set, get) => ({
  ghost: null,
  durations: DEFAULT_DURATIONS,
  draft: null,
  technicianName: null,
  isFullDay: false,
  isGhostInvalid: false,

  setGhost: (ghost) => set({ ghost }),
  setGhostInvalid: (v) => set({ isGhostInvalid: v }),

  commitGhost: () =>
    set((state) => ({
      ghost: state.ghost ? { ...state.ghost, committed: true } : null,
    })),

  clearGhost: () => {
    const { ghost } = get()
    if (ghost?.committed) return
    set({ ghost: null })
  },

  setDurations: (durations) => {
    const { draft, isFullDay } = get()
    // Full-day mode owns the durations — don't let collision logic override them
    if (isFullDay) return
    set({
      durations,
      draft: draft ? { ...draft, ...durations } : null,
    })
  },

  resetDurations: (durations) => set({ durations }),

  finalizeDraft: ({ technicianId, technicianName, anfahrtStartIso, durations, workOrderId, tradeCode, description = null, isFullDay = false }) =>
    set({
      isFullDay,
      draft: {
        workOrderId,
        assignedTechnicianId: Number(technicianId),
        date: anfahrtStartIso,
        tradeCode,
        ...durations,
        description,
      },
      technicianName,
    }),

  updatePosition: (anfahrtStartIso) =>
    set((state) => ({
      ghost: state.ghost ? { ...state.ghost, anfahrtStartIso } : null,
      draft: state.draft ? { ...state.draft, date: anfahrtStartIso } : null,
    })),

  updateTradeCode: (tradeCode) =>
    set((state) => ({
      draft: state.draft ? { ...state.draft, tradeCode } : null,
    })),

  updateDescription: (description) =>
    set((state) => ({
      draft: state.draft ? { ...state.draft, description } : null,
    })),

  setFullDay: (v, { shiftStart, shiftEnd }) => {
    if (!v) {
      set({ isFullDay: false })
      return
    }
    const { draft, ghost } = get()
    if (!draft) {
      set({ isFullDay: true })
      return
    }
    const dayIso = draft.date.slice(0, 10)
    const anfahrtStartIso = buildFullDayAnfahrtStart(dayIso, shiftStart)
    const durations: DefaultDurations = {
      plannedTravelDuration: 30,
      plannedWorkDuration: buildFullDayWorkDuration(shiftStart, shiftEnd),
      plannedDepartureDuration: 30,
    }
    set({
      isFullDay: true,
      durations,
      ghost: ghost ? { ...ghost, anfahrtStartIso } : null,
      draft: { ...draft, date: anfahrtStartIso, ...durations },
    })
  },

  moveFullDayToTechnician: ({ technicianId, technicianName, shiftStart, shiftEnd }) => {
    const { draft, ghost } = get()
    if (!draft || !ghost) return
    const dayIso = draft.date.slice(0, 10)
    const anfahrtStartIso = buildFullDayAnfahrtStart(dayIso, shiftStart)
    const durations: DefaultDurations = {
      plannedTravelDuration: 30,
      plannedWorkDuration: buildFullDayWorkDuration(shiftStart, shiftEnd),
      plannedDepartureDuration: 30,
    }
    set({
      durations,
      ghost: { ...ghost, technicianId: String(technicianId), anfahrtStartIso, committed: true },
      draft: {
        ...draft,
        assignedTechnicianId: technicianId,
        date: anfahrtStartIso,
        ...durations,
      },
      technicianName,
    })
  },

  clearAll: () =>
    set({
      ghost: null,
      durations: DEFAULT_DURATIONS,
      draft: null,
      technicianName: null,
      isFullDay: false,
      isGhostInvalid: false,
    }),
}))
